import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  authStateAtom,
  setAuthUserAtom,
  setAuthLoadingAtom,
  setAuthErrorAtom,
  clearAuthAtom,
} from '@/stores/auth';
import { AuthService } from '@/lib/auth/service';
import { apiClient } from '@/lib/api/client';
import { LoginCredentials } from '@/lib/auth/types';
import { setSession, clearSession } from '@/lib/auth/session';

// Create auth service instance
const authService = new AuthService(apiClient);

export function useAuth() {
  const authState = useAtomValue(authStateAtom);
  const setAuthUser = useSetAtom(setAuthUserAtom);
  const setAuthLoading = useSetAtom(setAuthLoadingAtom);
  const setAuthError = useSetAtom(setAuthErrorAtom);
  const clearAuth = useSetAtom(clearAuthAtom);
  const navigate = useNavigate();

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      try {
        setAuthLoading(true);
        setAuthError(null);

        const response = await authService.login(credentials);

        // Persist the whole session (tokens + user) through the single writer,
        // then mirror the user into React state.
        setSession({
          user: response.user,
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
        });
        setAuthUser(response.user);

        // Navigate to dashboard
        navigate({ to: '/admin/dashboard' });
      } catch (error: any) {
        // Extract error message from API response or fallback
        const message = error?.response?.data?.message ||
                        (error instanceof Error ? error.message : 'Login failed');
        setAuthError(message);
        throw error;
      } finally {
        setAuthLoading(false);
      }
    },
    [setAuthLoading, setAuthError, setAuthUser, navigate]
  );

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear the persisted session, then the React state, then navigate.
      clearSession();
      clearAuth();
      navigate({ to: '/login' });
    }
  }, [clearAuth, navigate]);

  return {
    ...authState,
    login,
    logout,
  };
}
